import { PropertyDetailsFields, initialPropertyDetails, PropertyDetails } from "../../components/Property/PropertyDetailsFields"
import { useEffect, useState } from "react"
import { X, Package, Pencil, PlayCircle, Save, Trash2, Expand, Shrink, LocateFixed, RefreshCcw } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { deleteGeneralProduct, getStockItemsByModel, updateGeneralProduct, uploadVehicleCarImagesToS3, uploadVehicleVideoToS3 } from "../../api/products/post"
import { toast } from "react-hot-toast"
import { useSelector } from "react-redux"
import { getStoreProductRules } from "../../utils/storeCategory"
import { getStoreExperience } from "../../utils/storeExperience"
import GestockAdVideoModal from "../../components/AdVideo/GestockAdVideoModal"
import {
  detectCurrentPropertyLocation,
  fetchNearbyPropertyAttractions,
  parseGoogleMapsLocation,
  parseNearbyAttractions,
  serializeNearbyAttractions,
} from "../../utils/propertyLocation"
import { normalizeAmenityList, suggestPropertyAmenities } from "../../utils/propertyAmenities"
import { suggestPropertyTitle } from "../../utils/propertyTitle"
import { SERVICE_DELIVERY_OPTIONS, SERVICE_PRICING_OPTIONS } from "../../utils/serviceOffering"

const ensureMinimumDelay = async (promise: Promise<any>, minimumMs = 2000) => {
  const [result] = await Promise.all([
    promise,
    new Promise((resolve) => setTimeout(resolve, minimumMs)),
  ])

  return result
}

type Props = {
  isOpen: boolean
  storeId?: string
  model: string | null
  onStockChanged?: () => void
  onClose: () => void
}

type CarImageItem = {
  key: string
  url: string
  previewUrl: string
  file?: File | null
  isNew?: boolean
}

const VEHICLE_CATEGORY_OPTIONS = [
  {
    value: "OCCASION",
    label: "Occasion",
  },
  {
    value: "LOCATION",
    label: "Location",
  },
  {
    value: "NEW",
    label: "Nouvelle voiture",
  },
] as const

const VEHICLE_CONDITION_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  OCCASION: [
    { value: "Occasion", label: "Occasion" },
    { value: "Tres bon etat", label: "Tres bon etat" },
    { value: "Bon etat", label: "Bon etat" },
    { value: "Revise", label: "Revise" },
  ],
  LOCATION: [
    { value: "Disponible a la location", label: "Disponible a la location" },
    { value: "Location libre", label: "Location libre" },
    { value: "Location avec chauffeur", label: "Location avec chauffeur" },
    { value: "Longue duree", label: "Longue duree" },
  ],
  NEW: [
    { value: "Neuve", label: "Neuve" },
    { value: "Zero km", label: "Zero km" },
    { value: "Jamais utilisee", label: "Jamais utilisee" },
    { value: "Sur commande", label: "Sur commande" },
  ],
}

const normalizeVehicleCategory = (value: string, fallback = "OCCASION") => {
  const normalized = String(value || "").trim().toUpperCase()
  return VEHICLE_CATEGORY_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : fallback
}

const inferVehicleCategory = (listingType: string, condition: string) => {
  const normalizedListingType = String(listingType || "").trim().toUpperCase()
  const normalizedCondition = String(condition || "").trim().toLowerCase()

  if (normalizedListingType === "RENT") {
    return "LOCATION"
  }

  if (["neuve", "new", "zero km", "0 km", "jamais utilisee", "jamais utilise"].some((hint) => normalizedCondition.includes(hint))) {
    return "NEW"
  }

  return "OCCASION"
}

export default function StockDetailsModal({
  isOpen,
  storeId,
  model,
  onStockChanged,
  onClose,
}: Props) {
  const { userdata } = useSelector((state: any) => state.UserReducer)
  const isAdmin = userdata?.user?.role === "ADMIN"
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [productId, setProductId] = useState("")
  const [productName, setProductName] = useState("")
  const [productIdentifier, setProductIdentifier] = useState("")
  const [buyPrice, setBuyPrice] = useState("")
  const [sellPrice, setSellPrice] = useState("")
  const [quantity, setQuantity] = useState("")
  const [listingType, setListingType] = useState("SALE")
  const [vehicleCategory, setVehicleCategory] = useState("OCCASION")
  const [dailyRentalPrice, setDailyRentalPrice] = useState("")
  const [description, setDescription] = useState("")
  const [propertyType, setPropertyType] = useState("APPARTEMENT")
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails>({...initialPropertyDetails})
  const [district, setDistrict] = useState("")
  const [bedrooms, setBedrooms] = useState("")
  const [bathrooms, setBathrooms] = useState("")
  const [areaSqm, setAreaSqm] = useState("")
  const [maxGuests, setMaxGuests] = useState("")
  const [monthlyRentalPrice, setMonthlyRentalPrice] = useState("")
  const [amenities, setAmenities] = useState("")
  const [nearbyAttractionsText, setNearbyAttractionsText] = useState("")
  const [furnished, setFurnished] = useState(true)
  const [latitude, setLatitude] = useState("")
  const [longitude, setLongitude] = useState("")
  const [mapUrl, setMapUrl] = useState("")
  const [detectingLocation, setDetectingLocation] = useState(false)
  const [loadingNearbyAttractions, setLoadingNearbyAttractions] = useState(false)
  const [year, setYear] = useState("")
  const [mileage, setMileage] = useState("")
  const [color, setColor] = useState("")
  const [transmission, setTransmission] = useState("")
  const [fuel, setFuel] = useState("")
  const [seats, setSeats] = useState("")
  const [availability, setAvailability] = useState("")
  const [location, setLocation] = useState("")
  const [condition, setCondition] = useState("")
  const [sourceName, setSourceName] = useState("")
  const [sourcePhone, setSourcePhone] = useState("")
  const [brand, setBrand] = useState("")
  const [productCategory, setProductCategory] = useState("")
  const [pricingType, setPricingType] = useState("FIXED")
  const [startingPrice, setStartingPrice] = useState("")
  const [durationMinutes, setDurationMinutes] = useState("")
  const [deliveryMode, setDeliveryMode] = useState("ONSITE")
  const [serviceArea, setServiceArea] = useState("")
  const [serviceTags, setServiceTags] = useState("")
  const [bookingEnabled, setBookingEnabled] = useState(true)
  const [featured, setFeatured] = useState(false)
  const [unitLabel, setUnitLabel] = useState("")
  const [stockAlertThreshold, setStockAlertThreshold] = useState("")
  const [supplierName, setSupplierName] = useState("")
  const [barcode, setBarcode] = useState("")
  const [storageLocation, setStorageLocation] = useState("")
  const [lotNumber, setLotNumber] = useState("")
  const [expiryDate, setExpiryDate] = useState("")
  const [vehicleImages, setVehicleImages] = useState<CarImageItem[]>([])
  const [vehicleVideoUrl, setVehicleVideoUrl] = useState("")
  const [vehicleVideoPreviewUrl, setVehicleVideoPreviewUrl] = useState("")
  const [vehicleVideoKey, setVehicleVideoKey] = useState("")
  const [vehicleVideoFile, setVehicleVideoFile] = useState<File | null>(null)
  const [pendingVideoPreviewUrl, setPendingVideoPreviewUrl] = useState("")
  const [isVideoExpanded, setIsVideoExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [adVideoModalOpen, setAdVideoModalOpen] = useState(false)
  const [adVideoGenerationMode, setAdVideoGenerationMode] = useState<"classic" | "cinematic_ai">("classic")
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const experience = getStoreExperience(userdata?.store)
  const currentStoreServiceArea = String(userdata?.store?.serviceArea || "")
  const isGymStore = experience.kind === "gym"
  const isServiceStore = experience.kind === "service"
  const { isCarStore, isPropertyStore, isMediaCatalogStore, minimumImages } =
    getStoreProductRules(userdata?.store)
  const acceptsSingleOptionalImage = !isMediaCatalogStore
  const isGymListing = /(gym|fitness|crossfit|workout|training|musculation|wellness|studio|club)/i.test(productCategory)
  const vehicleConditionOptions = VEHICLE_CONDITION_OPTIONS[vehicleCategory] || VEHICLE_CONDITION_OPTIONS.OCCASION
  const carListingTypeOptions =
    vehicleCategory === "LOCATION"
      ? [
          { value: "RENT", label: "Location" },
          { value: "BOTH", label: "Location & Vente" },
        ]
      : [
          { value: "SALE", label: "Vente" },
          { value: "BOTH", label: "Vente & Location" },
        ]

  const applyGymPreset = () => {
    setProductCategory("Gym / Fitness")
    setUnitLabel("membership")
    setAvailability("OUI")

    if (!amenities.trim()) {
      setAmenities("Strength zone, Cardio zone, Showers, Coach")
    }

    if (!description.trim()) {
      setDescription("Salle de sport avec espaces cardio, musculation et accompagnement coaching.")
    }
  }

  const handleSuggestPropertyAmenities = () => {
    const merged = [
      ...normalizeAmenityList(amenities),
      ...suggestPropertyAmenities({
        amenities,
        areaSqm,
        bathrooms,
        bedrooms,
        furnished,
        listingType,
        maxGuests,
        propertyType,
      }),
    ]

    const deduped = Array.from(new Set(merged.map((item) => item.trim()).filter(Boolean)))
    setAmenities(deduped.join(", "))
    toast.success("Equipements suggeres. Ajustez la liste avant enregistrement.")
  }

  const handleSuggestPropertyTitle = () => {
    const suggestion = suggestPropertyTitle({
      bedrooms,
      district,
      furnished,
      listingType,
      maxGuests,
      propertyType,
    })

    if (!suggestion) {
      toast.error("Ajoutez plus d'informations sur le bien pour suggerer un titre.")
      return
    }

    setProductName(suggestion)
    toast.success("Titre suggere. Vous pouvez encore le modifier.")
  }

  useEffect(() => {
    if (!vehicleVideoFile) {
      setPendingVideoPreviewUrl("")
      return
    }

    const localPreview = URL.createObjectURL(vehicleVideoFile)
    setPendingVideoPreviewUrl(localPreview)

    return () => {
      URL.revokeObjectURL(localPreview)
    }
  }, [vehicleVideoFile])

  useEffect(() => {
    if (!isOpen || !storeId || !model) return

    const fetchItems = async () => {
      try {
        setLoading(true)
        setError("")
        const res = await getStockItemsByModel(storeId, model)
        const data = res?.data || res?.items || res || []
        const list = Array.isArray(data) ? data : []
        const firstItem = list[0]
        const totalQuantity = list.reduce(
          (sum: number, item: any) => sum + Number(item?.quantity || 0),
          0
        )
        const totalBuyValue = list.reduce(
          (sum: number, item: any) =>
            sum + Number(item?.buyPrice || 0) * Number(item?.quantity || 0),
          0
        )
        const averageBuyPrice =
          totalQuantity > 0 ? totalBuyValue / totalQuantity : Number(firstItem?.buyPrice || 0)

        setItems(list)
        setProductId(String(firstItem?.productId || ""))
        setProductName(String(firstItem?.productName || model || ""))
        setProductIdentifier(String(firstItem?.identifier || ""))
        setBuyPrice(
          Number.isFinite(averageBuyPrice) ? String(Math.round(averageBuyPrice)) : ""
        )
        setSellPrice(firstItem?.sellPrice !== undefined ? String(firstItem.sellPrice) : "")
        setQuantity(String(totalQuantity || 0))
        setListingType(String(firstItem?.listingType || "SALE"))
        setVehicleCategory(
          normalizeVehicleCategory(
            String(firstItem?.vehicleCategory || ""),
            inferVehicleCategory(
              String(firstItem?.listingType || "SALE"),
              String(firstItem?.condition || "")
            )
          )
        )
        setDailyRentalPrice(
          firstItem?.dailyRentalPrice !== undefined ? String(firstItem.dailyRentalPrice) : ""
        )
        setDescription(String(firstItem?.description || ""))
        setPropertyType(String(firstItem?.propertyType || "APPARTEMENT"))
        setPropertyDetails({ listingSection: "AUTO", publicationStatus: "PUBLISHED", ...firstItem?.propertyDetails })
        setDistrict(String(firstItem?.district || ""))
        setBedrooms(String(firstItem?.bedrooms || ""))
        setBathrooms(String(firstItem?.bathrooms || ""))
        setAreaSqm(String(firstItem?.areaSqm || ""))
        setMaxGuests(String(firstItem?.maxGuests || ""))
        setMonthlyRentalPrice(
          firstItem?.monthlyRentalPrice !== undefined ? String(firstItem.monthlyRentalPrice) : ""
        )
        setAmenities(
          Array.isArray(firstItem?.amenities)
            ? firstItem.amenities.join(", ")
            : String(firstItem?.amenities || "")
        )
        setNearbyAttractionsText(
          serializeNearbyAttractions(
            Array.isArray(firstItem?.nearbyAttractions) ? firstItem.nearbyAttractions : []
          )
        )
        setFurnished(Boolean(firstItem?.furnished))
        setLatitude(firstItem?.latitude !== undefined && firstItem?.latitude !== null ? String(firstItem.latitude) : "")
        setLongitude(firstItem?.longitude !== undefined && firstItem?.longitude !== null ? String(firstItem.longitude) : "")
        setYear(String(firstItem?.year || ""))
        setMileage(String(firstItem?.mileage || ""))
        setColor(String(firstItem?.color || ""))
        setTransmission(String(firstItem?.transmission || ""))
        setFuel(String(firstItem?.fuel || ""))
        setSeats(String(firstItem?.seats || ""))
        setAvailability(String(firstItem?.availability || ""))
        setLocation(String(firstItem?.location || ""))
        setCondition(String(firstItem?.condition || ""))
        setSourceName(String(firstItem?.sourceName || ""))
        setSourcePhone(String(firstItem?.sourcePhone || ""))
        setBrand(String(firstItem?.brand || ""))
        setProductCategory(String(firstItem?.productCategory || ""))
        setPricingType(String(firstItem?.pricingType || "FIXED"))
        setStartingPrice(firstItem?.startingPrice !== undefined ? String(firstItem.startingPrice) : "")
        setDurationMinutes(firstItem?.durationMinutes !== undefined ? String(firstItem.durationMinutes) : "")
        setDeliveryMode(String(firstItem?.deliveryMode || "ONSITE"))
        setServiceArea(String(firstItem?.serviceArea || currentStoreServiceArea || ""))
        setServiceTags(
          Array.isArray(firstItem?.serviceTags)
            ? firstItem.serviceTags.join(", ")
            : String(firstItem?.serviceTags || "")
        )
        setBookingEnabled(firstItem?.bookingEnabled !== undefined ? Boolean(firstItem.bookingEnabled) : true)
        setFeatured(Boolean(firstItem?.featured))
        setUnitLabel(String(firstItem?.unitLabel || ""))
        setStockAlertThreshold(firstItem?.stockAlertThreshold !== undefined ? String(firstItem.stockAlertThreshold) : "")
        setSupplierName(String(firstItem?.supplierName || ""))
        setBarcode(String(firstItem?.barcode || ""))
        setStorageLocation(String(firstItem?.storageLocation || ""))
        setLotNumber(String(firstItem?.lotNumber || ""))
        setExpiryDate(String(firstItem?.expiryDate || ""))
        const imageUrls = Array.isArray(firstItem?.imageUrls)
          ? firstItem.imageUrls
          : [firstItem?.imageUrl || firstItem?.coverImageUrl].filter(Boolean)
        const imageKeys = Array.isArray(firstItem?.imageKeys)
          ? firstItem.imageKeys
          : [firstItem?.coverImageKey].filter(Boolean)
        const imagePreviewUrls = Array.isArray(firstItem?.imagePreviewUrls)
          ? firstItem.imagePreviewUrls
          : [firstItem?.imagePreviewUrl || firstItem?.imageUrl || firstItem?.coverImageUrl].filter(Boolean)

        setVehicleImages(
          imageUrls.map((url: string, index: number) => ({
            url: String(url || ""),
            key: String(imageKeys[index] || ""),
            previewUrl: String(imagePreviewUrls[index] || url || ""),
          })).filter((item: CarImageItem) => item.url || item.previewUrl || item.key)
        )
        setVehicleVideoUrl(String(firstItem?.videoUrl || firstItem?.video || ""))
        setVehicleVideoPreviewUrl(
          String(firstItem?.videoPreviewUrl || firstItem?.signedVideoUrl || firstItem?.videoUrl || "")
        )
        setVehicleVideoKey(String(firstItem?.videoKey || firstItem?.s3VideoKey || ""))
      } catch (e: any) {
        setError(e?.message || "Erreur lors du chargement")
      } finally {
        setLoading(false)
      }
    }

    fetchItems()
  }, [currentStoreServiceArea, isOpen, model, storeId])

  useEffect(() => {
    if (!isOpen) {
      setItems([])
      setError("")
      setProductId("")
      setProductName("")
      setProductIdentifier("")
      setBuyPrice("")
      setSellPrice("")
      setQuantity("")
      setListingType("SALE")
      setVehicleCategory("OCCASION")
      setDailyRentalPrice("")
      setDescription("")
      setPropertyType("APPARTEMENT")
      setDistrict("")
      setBedrooms("")
      setBathrooms("")
      setAreaSqm("")
      setMaxGuests("")
      setMonthlyRentalPrice("")
      setAmenities("")
      setNearbyAttractionsText("")
      setFurnished(true)
      setLatitude("")
      setLongitude("")
      setMapUrl("")
      setDetectingLocation(false)
      setLoadingNearbyAttractions(false)
      setYear("")
      setMileage("")
      setColor("")
      setTransmission("")
      setFuel("")
      setSeats("")
      setAvailability("")
      setLocation("")
      setCondition("")
      setSourceName("")
      setSourcePhone("")
      setBrand("")
      setProductCategory("")
      setPricingType("FIXED")
      setStartingPrice("")
      setDurationMinutes("")
      setDeliveryMode("ONSITE")
      setServiceArea("")
      setServiceTags("")
      setBookingEnabled(true)
      setFeatured(false)
      setUnitLabel("")
      setStockAlertThreshold("")
      setSupplierName("")
      setBarcode("")
      setStorageLocation("")
      setLotNumber("")
      setExpiryDate("")
      setVehicleImages([])
      setVehicleVideoUrl("")
      setVehicleVideoPreviewUrl("")
      setVehicleVideoKey("")
      setVehicleVideoFile(null)
      setPendingVideoPreviewUrl("")
      setIsVideoExpanded(false)
      setIsEditing(false)
      setSaving(false)
      setDeleting(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isCarStore) return

    const allowedListingTypes =
      vehicleCategory === "LOCATION" ? ["RENT", "BOTH"] : ["SALE", "BOTH"]

    if (!allowedListingTypes.includes(listingType)) {
      setListingType(vehicleCategory === "LOCATION" ? "RENT" : "SALE")
    }

    if (!vehicleConditionOptions.some((option) => option.value === condition)) {
      setCondition(vehicleConditionOptions[0]?.value || "")
    }
  }, [condition, isCarStore, listingType, vehicleCategory, vehicleConditionOptions])

  const totalQty = items.reduce((sum, i) => sum + Number(i.quantity || 1), 0)
  const totalBuy = items.reduce(
    (sum, i) => sum + Number(i.buyPrice || 0) * Number(i.quantity || 1),
    0
  )
  const totalSell = items.reduce(
    (sum, i) => sum + Number(i.sellPrice || 0) * Number(i.quantity || 1),
    0
  )
  const listingTypeLabel =
    listingType === "RENT" ? "Location" : listingType === "BOTH" ? "Vente & Location" : "Vente"
  const modalAccentButtonClass = isPropertyStore
    ? "bg-emerald-600 hover:bg-emerald-700"
    : isCarStore
      ? "bg-orange-500 hover:bg-orange-600"
      : "bg-blue-600 hover:bg-blue-700"

  const handleImageFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (!files.length) return

    const invalidFile = files.find((file) => !file.type.startsWith("image/"))
    if (invalidFile) {
      const message = "Tous les fichiers doivent etre des images"
      setError(message)
      toast.error(message)
      event.target.value = ""
      return
    }

    const oversizedFile = files.find((file) => file.size > 15 * 1024 * 1024)
    if (oversizedFile) {
      const message = "Chaque image doit etre inferieure a 15 MB"
      setError(message)
      toast.error(message)
      event.target.value = ""
      return
    }

    if (acceptsSingleOptionalImage && files.length > 1) {
      const message = "Un seul visuel est autorise pour ce produit"
      setError(message)
      toast.error(message)
      event.target.value = ""
      return
    }

    const nextImages = files.map((file) => ({
      key: "",
      url: "",
      previewUrl: URL.createObjectURL(file),
      file,
      isNew: true,
    }))

    setError("")
    setVehicleImages((current) => {
      if (!acceptsSingleOptionalImage) {
        return [...current, ...nextImages]
      }

      current.forEach((image) => {
        if (image?.isNew && image.previewUrl) {
          URL.revokeObjectURL(image.previewUrl)
        }
      })

      return nextImages.slice(0, 1)
    })
    event.target.value = ""
  }

  const handleRemoveImage = (indexToRemove: number) => {
    setVehicleImages((current) => {
      const imageToRemove = current[indexToRemove]
      if (imageToRemove?.isNew && imageToRemove.previewUrl) {
        URL.revokeObjectURL(imageToRemove.previewUrl)
      }

      return current.filter((_, index) => index !== indexToRemove)
    })
  }

  const handleMakeCoverImage = (indexToMove: number) => {
    setVehicleImages((current) => {
      if (indexToMove <= 0 || indexToMove >= current.length) return current
      const nextImages = [...current]
      const [selectedImage] = nextImages.splice(indexToMove, 1)
      nextImages.unshift(selectedImage)
      return nextImages
    })
  }

  const handleVideoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    if (!file) return

    if (!file.type.startsWith("video/")) {
      const message = "Le fichier selectionne doit etre une video"
      setError(message)
      toast.error(message)
      event.target.value = ""
      return
    }

    if (file.size > 250 * 1024 * 1024) {
      const message = "La video ne doit pas depasser 250 MB"
      setError(message)
      toast.error(message)
      event.target.value = ""
      return
    }

    setError("")
    setVehicleVideoFile(file)
    event.target.value = ""
  }

  const handleRemoveVideo = () => {
    setVehicleVideoFile(null)
    setPendingVideoPreviewUrl("")
    setVehicleVideoUrl("")
    setVehicleVideoPreviewUrl("")
    setVehicleVideoKey("")
  }

  const handleDetectLocation = async () => {
    try {
      setDetectingLocation(true)
      setError("")
      const detected = await detectCurrentPropertyLocation()
      setLatitude(String(detected.latitude))
      setLongitude(String(detected.longitude))
      if (detected.locationLabel && !location.trim()) setLocation(detected.locationLabel)
      if (detected.district && !district.trim()) setDistrict(detected.district)
      toast.success("Position détectée avec succès")
    } catch (e: any) {
      const message = e?.message || "Impossible de détecter la position"
      setError(message)
      toast.error(message)
    } finally {
      setDetectingLocation(false)
    }
  }

  const handleMapUrlExtract = () => {
    const parsed = parseGoogleMapsLocation(mapUrl)

    if (!parsed) {
      const message = "Impossible d'extraire des coordonnees depuis ce lien Google Maps."
      setError(message)
      toast.error(message)
      return
    }

    setError("")
    setLatitude(String(parsed.latitude))
    setLongitude(String(parsed.longitude))
    if (parsed.locationLabel && !location.trim()) setLocation(parsed.locationLabel)
    toast.success("Coordonnees extraites depuis le lien Google Maps")
  }

  const handleFetchNearbyAttractions = async () => {
    const parsedLatitude = Number(latitude)
    const parsedLongitude = Number(longitude)

    if (!Number.isFinite(parsedLatitude) || !Number.isFinite(parsedLongitude)) {
      const fallbackCoords = parseGoogleMapsLocation(mapUrl)

      if (!fallbackCoords) {
        const message = "Ajoutez d'abord un geocode ou extrayez un lien Google Maps avant de charger les attractions."
        setError(message)
        toast.error(message)
        return
      }

      setLatitude(String(fallbackCoords.latitude))
      setLongitude(String(fallbackCoords.longitude))
    }

    const baseLatitude = Number.isFinite(parsedLatitude)
      ? parsedLatitude
      : Number(parseGoogleMapsLocation(mapUrl)?.latitude)
    const baseLongitude = Number.isFinite(parsedLongitude)
      ? parsedLongitude
      : Number(parseGoogleMapsLocation(mapUrl)?.longitude)

    try {
      setLoadingNearbyAttractions(true)
      setError("")

      const fetched = await fetchNearbyPropertyAttractions(baseLatitude, baseLongitude)
      if (!fetched.length) {
        toast("Aucune attraction trouvee a proximite.")
        return
      }

      const mergedMap = new Map<string, { name: string; category: string; distance: string }>()
      for (const item of parseNearbyAttractions(nearbyAttractionsText)) {
        mergedMap.set(`${item.name.toLowerCase()}|${item.category.toLowerCase()}`, item)
      }
      for (const item of fetched) {
        mergedMap.set(`${item.name.toLowerCase()}|${item.category.toLowerCase()}`, item)
      }

      setNearbyAttractionsText(serializeNearbyAttractions(Array.from(mergedMap.values())))
      toast.success(`${fetched.length} attraction(s) ajoutee(s) automatiquement`)
    } catch (e: any) {
      const message = e?.message || "Impossible de recuperer les attractions a proximite"
      setError(message)
      toast.error(message)
    } finally {
      setLoadingNearbyAttractions(false)
    }
  }

  const handleSave = async () => {
    if (!storeId || !productId) return
    if (!productName.trim() || !productIdentifier.trim()) {
      setError("Nom et référence requis")
      return
    }
    if (Number(quantity) <= 0) {
      setError("La quantité doit être supérieure à 0")
      return
    }
    if (isMediaCatalogStore && vehicleImages.length < minimumImages) {
      setError(`Ajoutez au moins ${minimumImages} images`)
      return
    }

    try {
      setSaving(true)
      setError("")

      const finalVehicleImages: CarImageItem[] = []

      if (vehicleImages.length) {
        const newImageFiles = vehicleImages
          .filter((image) => image.isNew && image.file)
          .map((image) => image.file as File)
        const uploadResults = newImageFiles.length
          ? await uploadVehicleCarImagesToS3(newImageFiles, storeId)
          : []

        let newUploadIndex = 0
        vehicleImages.forEach((image) => {
          if (image.isNew) {
            const uploadedImage = uploadResults[newUploadIndex]
            newUploadIndex += 1

            if (!uploadedImage) return

            finalVehicleImages.push({
              key: uploadedImage?.coverImageKey || "",
              url: uploadedImage?.imageUrl || uploadedImage?.coverImageUrl || "",
              previewUrl: image.previewUrl || uploadedImage?.imageUrl || uploadedImage?.coverImageUrl || "",
            })
            return
          }

          finalVehicleImages.push(image)
        })
      }

      const nextImageUrls = finalVehicleImages.map((image) => image.url).filter(Boolean)
      const nextImageKeys = finalVehicleImages.map((image) => image.key).filter(Boolean)

      let nextVideoUrl = vehicleVideoUrl
      let nextVideoKey = vehicleVideoKey

      if (isCarStore && vehicleVideoFile) {
        const uploadedVideo = await uploadVehicleVideoToS3(vehicleVideoFile, storeId)
        nextVideoUrl = uploadedVideo?.videoUrl || ""
        nextVideoKey = uploadedVideo?.videoKey || ""
      }

      await ensureMinimumDelay(
        updateGeneralProduct(storeId, productId, {
          name: productName.trim(),
          buyPrice: Number(buyPrice || 0),
          sellPrice: Number(sellPrice || 0),
          quantity: Number(quantity || 0),
          offeringType: isServiceStore ? "SERVICE" : "PRODUCT",
          vehicleCategory,
          listingType,
          dailyRentalPrice: Number(dailyRentalPrice || 0),
          description,
          pricingType,
          startingPrice: Number(startingPrice || 0),
          durationMinutes: Number(durationMinutes || 0),
          deliveryMode,
          serviceArea,
          bookingEnabled,
          featured,
          serviceTags: serviceTags.split(",").map((item) => item.trim()).filter(Boolean),
          propertyType,
          ...(isPropertyStore ? { propertyDetails } : {}),
          district,
          bedrooms: Number(bedrooms || 0),
          bathrooms: Number(bathrooms || 0),
          areaSqm: Number(areaSqm || 0),
          maxGuests: Number(maxGuests || 0),
          monthlyRentalPrice: Number(monthlyRentalPrice || 0),
          amenities: amenities.split(",").map((item) => item.trim()).filter(Boolean),
          nearbyAttractions: parseNearbyAttractions(nearbyAttractionsText),
          furnished,
          latitude: latitude ? Number(latitude) : null,
          longitude: longitude ? Number(longitude) : null,
          year,
          mileage,
          color,
          transmission,
          fuel,
          seats,
          availability,
          location,
          condition,
          sourceName,
          sourcePhone,
          brand,
          productCategory,
          unitLabel,
          stockAlertThreshold: Number(stockAlertThreshold || 0),
          supplierName,
          barcode,
          storageLocation,
          lotNumber,
          expiryDate: expiryDate || undefined,
          imageUrls: nextImageUrls,
          imageKeys: nextImageKeys,
          coverImageUrl: nextImageUrls[0] || "",
          imageUrl: nextImageUrls[0] || "",
          coverImageKey: nextImageKeys[0] || "",
          videoUrl: nextVideoUrl,
          videoKey: nextVideoKey,
        })
      )

      toast.success(`${isServiceStore ? "Service" : "Produit"} mis à jour`)
      setVehicleImages(finalVehicleImages)
      setVehicleVideoUrl(nextVideoUrl)
      setVehicleVideoKey(nextVideoKey)
      setVehicleVideoPreviewUrl(pendingVideoPreviewUrl || vehicleVideoPreviewUrl || nextVideoUrl)
      setVehicleVideoFile(null)
      setPendingVideoPreviewUrl("")
      setItems((current) =>
        current.map((item) => ({
          ...item,
          productName: productName.trim(),
          buyPrice: Number(buyPrice || 0),
          sellPrice: Number(sellPrice || 0),
          quantity: Number(quantity || 0),
          offeringType: isServiceStore ? "SERVICE" : "PRODUCT",
          vehicleCategory,
          listingType,
          dailyRentalPrice: Number(dailyRentalPrice || 0),
          description,
          pricingType,
          startingPrice: Number(startingPrice || 0),
          durationMinutes: Number(durationMinutes || 0),
          deliveryMode,
          serviceArea,
          bookingEnabled,
          featured,
          serviceTags: serviceTags.split(",").map((item) => item.trim()).filter(Boolean),
          propertyType,
          ...(isPropertyStore ? { propertyDetails } : {}),
          district,
          bedrooms: Number(bedrooms || 0),
          bathrooms: Number(bathrooms || 0),
          areaSqm: Number(areaSqm || 0),
          maxGuests: Number(maxGuests || 0),
          monthlyRentalPrice: Number(monthlyRentalPrice || 0),
          amenities: amenities.split(",").map((item) => item.trim()).filter(Boolean),
          nearbyAttractions: parseNearbyAttractions(nearbyAttractionsText),
          furnished,
          latitude: latitude ? Number(latitude) : null,
          longitude: longitude ? Number(longitude) : null,
          brand,
          productCategory,
          unitLabel,
          stockAlertThreshold: Number(stockAlertThreshold || 0),
          supplierName,
          barcode,
          storageLocation,
          lotNumber,
          expiryDate: expiryDate || undefined,
          imageUrls: nextImageUrls,
          imageKeys: nextImageKeys,
          imageUrl: nextImageUrls[0] || "",
          coverImageUrl: nextImageUrls[0] || "",
          videoUrl: nextVideoUrl,
          videoKey: nextVideoKey,
        }))
      )
      setProductIdentifier((currentIdentifier) => currentIdentifier.trim().toUpperCase())
      setProductName((currentName) => currentName.trim())
      setIsEditing(false)
      onStockChanged?.()
    } catch (e: any) {
      const message = e?.message || "Erreur lors de la modification"
      setError(message)
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!storeId || !productId || !isAdmin) return

    try {
      setDeleting(true)
      setError("")
      const res = await ensureMinimumDelay(deleteGeneralProduct(storeId, productId))
      toast.success(res?.message || `${isServiceStore ? "Service" : "Produit"} supprimé`)
      onStockChanged?.()
      onClose()
    } catch (e: any) {
      const message = e?.message || "Erreur lors de la suppression"
      setError(message)
      toast.error(message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-3 py-3 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="max-h-[calc(100vh-1.5rem)] w-full max-w-3xl overflow-y-auto rounded-2xl border border-white/40 bg-white/95 p-4 space-y-4 shadow-2xl sm:max-h-[calc(100vh-3rem)] sm:rounded-3xl sm:p-6 sm:space-y-5"
            initial={{ scale: 0.9, y: 40 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 40 }}
          >
            {/* Header */}
            <div className={`rounded-2xl bg-gradient-to-br ${experience.stock.heroClass} p-4 text-white sm:rounded-3xl sm:p-5`}>
              <div className="flex items-start justify-between gap-3 sm:gap-4">
                <div>
                <p className="inline-flex rounded-full bg-white/15 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                  {experience.badge}
                </p>
                <h2 className="mt-3 text-lg font-semibold">
                  {isCarStore ? "Details du vehicule" : isPropertyStore ? "Details du bien" : isServiceStore ? "Details du service" : "Details du stock"}
                </h2>
                <p className="text-sm text-white/80">
                  {productName || model || "Produit"}
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-xl bg-white/10 p-2 text-white transition hover:bg-white/20"
              >
                <X />
              </button>
              </div>
            </div>

            <div className={`rounded-2xl border p-4 space-y-4 sm:rounded-3xl ${experience.stock.panelClass}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{isServiceStore ? "Service" : "Produit"}</p>
                  <p className="text-xs text-slate-500">
                    {isPropertyStore ? "Modifiez les informations du bien" : isServiceStore ? "Modifiez les parametres de l'offre service" : "Modifiez les informations du produit"}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
                  <button
                    onClick={() => {
                      setAdVideoGenerationMode("classic")
                      setAdVideoModalOpen(true)
                    }}
                    disabled={!productId || saving || deleting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-100 disabled:opacity-50 sm:w-auto sm:py-1.5"
                  >
                    <PlayCircle size={15} />
                    Ai Ad Video
                  </button>
                  <button
                    onClick={() => {
                      setAdVideoGenerationMode("cinematic_ai")
                      setAdVideoModalOpen(true)
                    }}
                    disabled={!productId || saving || deleting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 sm:w-auto sm:py-1.5"
                  >
                    <PlayCircle size={15} />
                    Ai Ad Video Pro
                  </button>
                  <button
                    onClick={() => setIsEditing((value) => !value)}
                    disabled={saving || deleting || !productId}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50 sm:w-auto sm:py-1.5"
                  >
                    <Pencil size={15} />
                    {isEditing ? "Fermer" : "Modifier"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs text-slate-500">Nom</label>
                    {isPropertyStore && isEditing && (
                      <button
                        type="button"
                        onClick={handleSuggestPropertyTitle}
                        disabled={saving}
                        className="text-xs font-medium text-emerald-700 hover:text-emerald-600 disabled:opacity-60"
                      >
                        Suggerer titre
                      </button>
                    )}
                  </div>
                  <input
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    disabled={!isEditing || saving}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Référence</label>
                  <input
                    value={productIdentifier}
                    disabled
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">{isServiceStore ? "Cout interne" : "Prix d'achat"}</label>
                  <input
                    type="number"
                    min={0}
                    value={buyPrice}
                    onChange={(e) => setBuyPrice(e.target.value)}
                    disabled={!isEditing || saving}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">{isServiceStore ? "Prix affiche" : "Prix de vente"}</label>
                  <input
                    type="number"
                    min={0}
                    value={sellPrice}
                    onChange={(e) => setSellPrice(e.target.value)}
                    disabled={!isEditing || saving}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">{isServiceStore ? "Capacite / slots" : "Quantité"}</label>
                  <input
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    disabled={!isEditing || saving}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">
                  {isAdmin
                    ? "Suppression autorisée si le produit n’a aucun historique de vente. Le stock restant sera supprimé avec le produit."
                    : "Seul un administrateur peut supprimer un produit."}
                </p>
                <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
                  {isEditing && (
                    <button
                      onClick={handleSave}
                      disabled={saving || deleting || !productId}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm text-white disabled:opacity-50 sm:w-auto ${modalAccentButtonClass}`}
                    >
                      <Save size={15} />
                      {saving ? "Enregistrement..." : "Enregistrer"}
                    </button>
                  )}
                  <button
                    onClick={handleDelete}
                    disabled={deleting || saving || !productId || !isAdmin}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                  >
                    <Trash2 size={15} />
                    {deleting ? "Suppression..." : "Supprimer"}
                  </button>
                </div>
              </div>

              {!isMediaCatalogStore && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Image du produit</p>
                      <p className="text-xs text-slate-500">
                        Optionnel. Une seule image peut etre associee au produit.
                      </p>
                    </div>
                    {isEditing && (
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100">
                        Ajouter une image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageFilesChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:max-w-xs">
                    {vehicleImages.map((image, index) => (
                      <div key={`${image.key || image.previewUrl || index}-${index}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                        <img
                          src={image.previewUrl || image.url}
                          alt={`Produit ${index + 1}`}
                          className="h-28 w-full object-cover"
                        />
                        <div className="space-y-2 border-t border-slate-200 px-2 py-2">
                          <p className="text-[11px] text-slate-500">Image principale</p>
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="rounded-md border border-red-200 px-2 py-1 text-[11px] text-red-600 hover:bg-red-50"
                            >
                              Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isMediaCatalogStore && (
                <div className="grid grid-cols-1 gap-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {isCarStore ? "Images du vehicule" : "Images du bien"}
                        </p>
                        <p className="text-xs text-slate-500">
                          La premiere image est utilisee comme couverture. Minimum {minimumImages} images.
                        </p>
                      </div>
                      {isEditing && (
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100">
                          Ajouter des images
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageFilesChange}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {vehicleImages.map((image, index) => (
                        <div key={`${image.key || image.previewUrl || index}-${index}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                          <img
                            src={image.previewUrl || image.url}
                            alt={`${isCarStore ? "Vehicule" : "Bien"} ${index + 1}`}
                            className="h-28 w-full object-cover"
                          />
                          <div className="space-y-2 border-t border-slate-200 px-2 py-2">
                            <p className="text-[11px] text-slate-500">
                              {index === 0 ? "Couverture" : `Image ${index + 1}`}
                            </p>
                            {isEditing && (
                              <div className="flex flex-wrap gap-2">
                                {index > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => handleMakeCoverImage(index)}
                                    className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
                                  >
                                    Mettre en couverture
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveImage(index)}
                                  className="rounded-md border border-red-200 px-2 py-1 text-[11px] text-red-600 hover:bg-red-50"
                                >
                                  Supprimer
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {isCarStore && (pendingVideoPreviewUrl || vehicleVideoPreviewUrl || vehicleVideoUrl) && (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                          <PlayCircle size={16} />
                          <span>Video 360</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {isEditing && (
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
                              Remplacer
                              <input
                                type="file"
                                accept="video/*"
                                onChange={handleVideoFileChange}
                                className="hidden"
                              />
                            </label>
                          )}
                          {isEditing && (pendingVideoPreviewUrl || vehicleVideoPreviewUrl || vehicleVideoUrl) && (
                            <button
                              type="button"
                              onClick={handleRemoveVideo}
                              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                            >
                              Supprimer
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setIsVideoExpanded((value) => !value)}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
                          >
                            {isVideoExpanded ? <Shrink size={14} /> : <Expand size={14} />}
                            {isVideoExpanded ? "Reduire" : "Agrandir"}
                          </button>
                        </div>
                      </div>
                      <video
                        src={pendingVideoPreviewUrl || vehicleVideoPreviewUrl || vehicleVideoUrl}
                        controls
                        className={`w-full rounded-xl border border-slate-200 bg-black object-contain transition-all ${
                          isVideoExpanded ? "max-h-[70vh]" : "max-h-64"
                        }`}
                      />
                      {vehicleVideoKey && (
                        <p className="mt-2 text-xs text-slate-500">
                          Cle S3: {vehicleVideoKey}
                        </p>
                      )}
                      {vehicleVideoUrl && (
                        <div className="mt-3">
                          <a
                            href={vehicleVideoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
                          >
                            Voir sur mavoiture.com
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {isCarStore && isEditing && !(pendingVideoPreviewUrl || vehicleVideoPreviewUrl || vehicleVideoUrl) && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Video 360</p>
                          <p className="text-xs text-slate-500">Optionnel. Ajoutez ou remplacez la video du vehicule.</p>
                        </div>
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100">
                          Ajouter une video
                          <input
                            type="file"
                            accept="video/*"
                            onChange={handleVideoFileChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {isCarStore && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                    <label className="text-xs text-slate-500">Categorie vehicule</label>
                    <select
                      value={vehicleCategory}
                      onChange={(e) => setVehicleCategory(normalizeVehicleCategory(e.target.value))}
                      disabled={!isEditing || saving}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                    >
                      {VEHICLE_CATEGORY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                    <div>
                    <label className="text-xs text-slate-500">Type d'annonce</label>
                    <select
                      value={listingType}
                      onChange={(e) => setListingType(e.target.value)}
                      disabled={!isEditing || saving}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                    >
                      {carListingTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                    <div>
                    <label className="text-xs text-slate-500">Prix location / jour</label>
                    <input type="number" min={0} value={dailyRentalPrice} onChange={(e) => setDailyRentalPrice(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Année</label>
                    <input value={year} onChange={(e) => setYear(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Kilométrage</label>
                    <input value={mileage} onChange={(e) => setMileage(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Couleur</label>
                    <input value={color} onChange={(e) => setColor(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Transmission</label>
                    <input value={transmission} onChange={(e) => setTransmission(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Carburant</label>
                    <input value={fuel} onChange={(e) => setFuel(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Places</label>
                    <input value={seats} onChange={(e) => setSeats(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Disponibilité</label>
                    <select
                      value={availability}
                      onChange={(e) => setAvailability(e.target.value)}
                      disabled={!isEditing || saving}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                    >
                      <option value="">Selectionner</option>
                      <option value="OUI">Oui</option>
                      <option value="NON">Non</option>
                    </select>
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Localisation</label>
                    <input value={location} onChange={(e) => setLocation(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Proprietaire / parc auto</label>
                    <input value={sourceName} onChange={(e) => setSourceName(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div>
                    <label className="text-xs text-slate-500">Telephone de reference</label>
                    <input value={sourcePhone} onChange={(e) => setSourcePhone(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                    <label className="text-xs text-slate-500">Etat</label>
                    <select value={condition} onChange={(e) => setCondition(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500">
                      {vehicleConditionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                    <label className="text-xs text-slate-500">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={!isEditing || saving}
                      rows={3}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    </div>
                  </div>
                  )}

                  {!isCarStore && !isPropertyStore && (
                    <div className="space-y-4">
                      {isServiceStore && (
                        <div className="rounded-2xl border border-cyan-100 bg-cyan-50/80 p-4">
                          <p className="text-sm font-semibold text-slate-800">Parametres de la prestation</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Ces champs alimentent la page services publique et la logique de devis.
                          </p>

                          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <div>
                              <label className="text-xs text-slate-500">Type de prix</label>
                              <select
                                value={pricingType}
                                onChange={(e) => setPricingType(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              >
                                {SERVICE_PRICING_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Prix de depart</label>
                              <input
                                type="number"
                                min={0}
                                value={startingPrice}
                                onChange={(e) => setStartingPrice(e.target.value)}
                                disabled={!isEditing || saving || pricingType !== "STARTING_AT"}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Duree estimee (min)</label>
                              <input
                                type="number"
                                min={0}
                                value={durationMinutes}
                                onChange={(e) => setDurationMinutes(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Mode de prestation</label>
                              <select
                                value={deliveryMode}
                                onChange={(e) => setDeliveryMode(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              >
                                {SERVICE_DELIVERY_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="sm:col-span-2">
                              <label className="text-xs text-slate-500">Zone de service</label>
                              <input
                                value={serviceArea}
                                onChange={(e) => setServiceArea(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                            </div>
                            <div className="sm:col-span-2 lg:col-span-3">
                              <label className="text-xs text-slate-500">Tags service</label>
                              <input
                                value={serviceTags}
                                onChange={(e) => setServiceTags(e.target.value)}
                                disabled={!isEditing || saving}
                                placeholder="Ex: nettoyage, bureaux, devis express"
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                            </div>
                          </div>

                          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                              <input type="checkbox" checked={bookingEnabled} onChange={(e) => setBookingEnabled(e.target.checked)} disabled={!isEditing || saving} />
                              Demande de devis / reservation active
                            </label>
                            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} disabled={!isEditing || saving} />
                              Mettre en avant ce service
                            </label>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <div>
                          <label className="text-xs text-slate-500">Marque</label>
                          <input value={brand} onChange={(e) => setBrand(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Categorie</label>
                          <input value={productCategory} onChange={(e) => setProductCategory(e.target.value)} disabled={!isEditing || saving} placeholder={isServiceStore ? "Ex: nettoyage, montage, assistance" : "Ex: Gym / Fitness"} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Unite de vente</label>
                          <input value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} disabled={!isEditing || saving} placeholder={isServiceStore ? "Ex: intervention, heure, forfait" : ""} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Seuil d'alerte</label>
                          <input type="number" min={0} value={stockAlertThreshold} onChange={(e) => setStockAlertThreshold(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Fournisseur principal</label>
                          <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Emplacement stock</label>
                          <input value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Code-barres / SKU secondaire</label>
                          <input value={barcode} onChange={(e) => setBarcode(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Numero de lot</label>
                          <input value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Date d'expiration</label>
                          <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                        </div>
                        <div className="sm:col-span-2 lg:col-span-3">
                          <label className="text-xs text-slate-500">Description</label>
                          <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={!isEditing || saving}
                            rows={3}
                            placeholder={isServiceStore ? "Precisez le contenu exact de la prestation, ce qui est inclus et le cadre du devis." : ""}
                            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                          />
                        </div>
                      </div>

                      {isGymStore && (
                        <div className="rounded-2xl border border-blue-100 bg-blue-50/80 p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-slate-800">Preset Gym / Fitness</p>
                              <p className="mt-1 text-xs text-slate-500">
                                Prépare une fiche visible dans GoalFit Nearby Gyms.
                              </p>
                            </div>
                            {isEditing && (
                              <button
                                type="button"
                                onClick={applyGymPreset}
                                disabled={saving}
                                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                              >
                                Appliquer le preset gym
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {isGymStore && isGymListing && (
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-slate-800">Localisation de la salle</p>
                              <p className="mt-1 text-xs text-slate-500">
                                Ces champs alimentent le classement Nearby Gyms dans GoalFit.
                              </p>
                            </div>
                            {isEditing && (
                              <button
                                type="button"
                                onClick={handleDetectLocation}
                                disabled={saving || detectingLocation}
                                className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                              >
                                <LocateFixed size={14} />
                                {detectingLocation ? "Détection..." : "Détecter la position"}
                              </button>
                            )}
                          </div>

                          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                              <label className="text-xs text-slate-500">Localisation visible</label>
                              <input
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Disponibilite</label>
                              <select
                                value={availability}
                                onChange={(e) => setAvailability(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              >
                                <option value="">Selectionner</option>
                                <option value="OUI">Oui</option>
                                <option value="NON">Non</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Latitude</label>
                              <input
                                type="number"
                                step="any"
                                value={latitude}
                                onChange={(e) => setLatitude(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Longitude</label>
                              <input
                                type="number"
                                step="any"
                                value={longitude}
                                onChange={(e) => setLongitude(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="text-xs text-slate-500">Equipements de la salle</label>
                              <input
                                value={amenities}
                                onChange={(e) => setAmenities(e.target.value)}
                                disabled={!isEditing || saving}
                                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {isPropertyStore && (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div className="lg:col-span-2"><PropertyDetailsFields value={propertyDetails} onChange={setPropertyDetails} disabled={!isEditing || saving} /></div>
                      <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-800">Annonce</p>
                          <span className="inline-flex rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-medium text-emerald-700">
                            {listingTypeLabel}
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className="text-xs text-slate-500">Type d'annonce</label>
                            <select
                              value={listingType}
                              onChange={(e) => setListingType(e.target.value)}
                              disabled={!isEditing || saving}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            >
                              <option value="SALE">Vente</option>
                              <option value="RENT">Location</option>
                              <option value="BOTH">Vente & Location</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Type de bien</label>
                            <select
                              value={propertyType}
                              onChange={(e) => setPropertyType(e.target.value)}
                              disabled={!isEditing || saving}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            >
                              <option value="APPARTEMENT">Appartement</option>
                              <option value="RESIDENCE_MEUBLEE">Residence meublee</option>
                              <option value="VILLA">Villa</option>
                              <option value="STUDIO">Studio</option>
                              <option value="IMMEUBLE">Immeuble</option>
                              <option value="VILLA_BASSE">Villa basse</option>
                              <option value="DUPLEX">Duplex</option>
                              <option value="GROS_OEUVRE">Gros œuvre</option>
                              <option value="CLES_EN_MAIN">Clés en main</option>
                              <option value="TERRAIN">Terrain</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Prix / jour</label>
                            <input
                              type="number"
                              min={0}
                              value={dailyRentalPrice}
                              onChange={(e) => setDailyRentalPrice(e.target.value)}
                              disabled={!isEditing || saving}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Prix / mois</label>
                            <input
                              type="number"
                              min={0}
                              value={monthlyRentalPrice}
                              onChange={(e) => setMonthlyRentalPrice(e.target.value)}
                              disabled={!isEditing || saving}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-xs text-slate-500">Description</label>
                            <textarea
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              disabled={!isEditing || saving}
                              rows={3}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-sm font-semibold text-slate-800">Localisation & capacite</p>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className="text-xs text-slate-500">Commune / quartier</label>
                            <input
                              value={district}
                              onChange={(e) => setDistrict(e.target.value)}
                              disabled={!isEditing || saving}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Localisation precise</label>
                            <input
                              value={location}
                              onChange={(e) => setLocation(e.target.value)}
                              disabled={!isEditing || saving}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            />
                          </div>
                          <div className="sm:col-span-2 rounded-2xl border border-emerald-100 bg-white p-3">
                            <label className="text-xs text-slate-500">Lien Google Maps / WhatsApp</label>
                            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                              <input
                                value={mapUrl}
                                onChange={(e) => setMapUrl(e.target.value)}
                                disabled={!isEditing || saving}
                                placeholder="Collez un lien maps.google.com ou google.com/maps"
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                              />
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={handleMapUrlExtract}
                                  disabled={saving || !mapUrl.trim()}
                                  className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                                >
                                  Extraire
                                </button>
                              )}
                            </div>
                            <p className="mt-2 text-xs text-slate-400">
                              Collez un lien partage Google Maps recu sur WhatsApp pour remplir automatiquement le geocode.
                            </p>
                          </div>
                          <div className="sm:col-span-2 rounded-2xl border border-emerald-100 bg-white p-3">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <label className="text-xs text-slate-500">Coordonnees geographiques</label>
                                <p className="mt-1 text-xs text-slate-400">
                                  Utilisez la detection automatique ou modifiez latitude et longitude.
                                </p>
                              </div>
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={handleDetectLocation}
                                  disabled={saving || detectingLocation}
                                  className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                                >
                                  <LocateFixed size={14} />
                                  {detectingLocation ? "Détection..." : "Détecter la position"}
                                </button>
                              )}
                            </div>
                            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <div>
                                <label className="text-xs text-slate-500">Latitude</label>
                                <input
                                  type="number"
                                  step="any"
                                  value={latitude}
                                  onChange={(e) => setLatitude(e.target.value)}
                                  disabled={!isEditing || saving}
                                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">Longitude</label>
                                <input
                                  type="number"
                                  step="any"
                                  value={longitude}
                                  onChange={(e) => setLongitude(e.target.value)}
                                  disabled={!isEditing || saving}
                                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                                />
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Surface m2</label>
                            <input type="number" min={0} value={areaSqm} onChange={(e) => setAreaSqm(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Voyageurs max</label>
                            <input type="number" min={0} value={maxGuests} onChange={(e) => setMaxGuests(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Chambres</label>
                            <input type="number" min={0} value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Salles de bain</label>
                            <input type="number" min={0} value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Bien meuble</label>
                            <label className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                              <input type="checkbox" checked={furnished} onChange={(e) => setFurnished(e.target.checked)} disabled={!isEditing || saving} />
                              {furnished ? "Oui" : "Non"}
                            </label>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Disponibilite</label>
                            <select
                              value={availability}
                              onChange={(e) => setAvailability(e.target.value)}
                              disabled={!isEditing || saving}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            >
                              <option value="">Selectionner</option>
                              <option value="OUI">Oui</option>
                              <option value="NON">Non</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-xs text-slate-500">Equipements</label>
                            <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <p className="text-xs text-slate-400">
                                Générez une base d'atouts commerciaux à partir de la fiche actuelle.
                              </p>
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={handleSuggestPropertyAmenities}
                                  disabled={saving}
                                  className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                                >
                                  Suggerer
                                </button>
                              )}
                            </div>
                            <input
                              value={amenities}
                              onChange={(e) => setAmenities(e.target.value)}
                              disabled={!isEditing || saving}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-xs text-slate-500">Attractions a proximite</label>
                            <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <p className="text-xs text-slate-400">
                                Chargez des suggestions automatiques puis ajustez les lignes si besoin.
                              </p>
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={handleFetchNearbyAttractions}
                                  disabled={saving || loadingNearbyAttractions}
                                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                                >
                                  <RefreshCcw size={14} />
                                  {loadingNearbyAttractions ? "Chargement..." : "Charger automatiquement"}
                                </button>
                              )}
                            </div>
                            <textarea
                              value={nearbyAttractionsText}
                              onChange={(e) => setNearbyAttractionsText(e.target.value)}
                              disabled={!isEditing || saving}
                              rows={4}
                              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                            />
                            <p className="mt-1 text-xs text-slate-400">Format: nom | categorie | distance, une ligne par attraction.</p>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Nom du proprietaire</label>
                            <input value={sourceName} onChange={(e) => setSourceName(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Telephone</label>
                            <input value={sourcePhone} onChange={(e) => setSourcePhone(e.target.value)} disabled={!isEditing || saving} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl border p-3">
                <p className="text-xs text-gray-500">Quantité</p>
                <p className="text-lg font-semibold text-gray-800">{totalQty}</p>
              </div>
              <div className="rounded-xl border p-3">
                <p className="text-xs text-gray-500">Valeur achat</p>
                <p className="text-lg font-semibold text-gray-800">
                  {totalBuy.toLocaleString()} FCFA
                </p>
              </div>
              <div className="rounded-xl border p-3">
                <p className="text-xs text-gray-500">Valeur vente</p>
                <p className="text-lg font-semibold text-gray-800">
                  {totalSell.toLocaleString()} FCFA
                </p>
              </div>
              <div className="rounded-xl border p-3 flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Package size={16} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Articles</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {items.length}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            {loading && (
              <div className="text-sm text-gray-500">Chargement…</div>
            )}
            {error && (
              <div className="text-sm text-red-600">{error}</div>
            )}

            {!loading && !error && (
              <div className="bg-white rounded-2xl border overflow-hidden">
                <div className="overflow-x-auto">
                <table className="min-w-[640px] w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-500">
                      <th className="px-4 py-3">Référence</th>
                      <th className="px-4 py-3">Quantité</th>
                      <th className="px-4 py-3">Prix achat</th>
                      <th className="px-4 py-3">Prix vente</th>
                      <th className="px-4 py-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((i: any, idx: number) => {
                      const ref =
                        i.identifier || i.sku || i.code || i.imei || "-"
                      const status =
                        i.status === "SOLD" ? "Vendu" : "En stock"
                      return (
                        <tr key={i._id || ref || idx} className="border-t">
                          <td className="px-4 py-3 text-gray-700">{ref}</td>
                          <td className="px-4 py-3">{i.quantity || 1}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {Number(i.buyPrice || 0).toLocaleString()} FCFA
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {i.sellPrice
                              ? `${Number(i.sellPrice || 0).toLocaleString()} FCFA`
                              : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                                i.status === "SOLD"
                                  ? "bg-gray-100 text-gray-600 border-gray-200"
                                  : "bg-green-50 text-green-700 border-green-200"
                              }`}
                            >
                              {status}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                    {items.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-6 text-center text-gray-500"
                        >
                          Aucun article trouvé
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      <GestockAdVideoModal
        isOpen={adVideoModalOpen}
        onClose={() => setAdVideoModalOpen(false)}
        storeId={storeId}
        productId={productId}
        generationMode={adVideoGenerationMode}
        productName={productName || model || "Produit"}
        productCategory={productCategory || (isCarStore ? "CAR" : isPropertyStore ? "PROPERTY" : "GENERAL")}
        images={vehicleImages.map((image) => ({
          key: image.key,
          url: image.url,
          previewUrl: image.previewUrl,
        }))}
        defaultPhone={sourcePhone}
      />
    </AnimatePresence>
  )
}
